import React, { useRef } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { NodeSelection } from "@tiptap/pm/state";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";

const MIN_IMAGE_WIDTH = 120;
export const DEFAULT_RESIZABLE_IMAGE_WIDTH = 440;
const VALID_IMAGE_LAYOUTS = new Set(["center", "left", "right"]);

function toPxNumber(raw) {
  if (raw == null) return null;
  const parsed = parseInt(String(raw).replace("px", ""), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeLayout(raw) {
  return VALID_IMAGE_LAYOUTS.has(raw) ? raw : "center";
}

function readLayoutFromElement(element) {
  const fromData = element.getAttribute("data-lh-image-layout");
  if (VALID_IMAGE_LAYOUTS.has(fromData)) return fromData;

  if (element.classList?.contains("lh6-image-align-left")) return "left";
  if (element.classList?.contains("lh6-image-align-right")) return "right";
  if (element.classList?.contains("lh6-image-align-center")) return "center";

  const floatValue = element.style?.float;
  if (floatValue === "left" || floatValue === "right") return floatValue;

  return "center";
}

const ResizableImageNodeView = ({ node, editor, getPos, selected, updateAttributes }) => {
  const wrapperRef = useRef(null);
  const resizeStateRef = useRef(null);

  const width = toPxNumber(node.attrs?.width);
  const layout = normalizeLayout(node.attrs?.layout);

  const selectNode = (event) => {
    if (event.target?.closest?.(".lh6-imageResizeHandle")) return;
    event.stopPropagation();

    const pos = getPos?.();
    if (typeof pos !== "number") return;

    if (editor?.view?.dispatch) {
      const tr = editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, pos));
      editor.view.dispatch(tr);
    }
  };

  const stopResize = () => {
    resizeStateRef.current = null;
    document.removeEventListener("mousemove", onResizeMove);
    document.removeEventListener("mouseup", stopResize);
    document.removeEventListener("touchmove", onResizeMove);
    document.removeEventListener("touchend", stopResize);
  };

  const onResizeMove = (event) => {
    const resizeState = resizeStateRef.current;
    if (!resizeState) return;

    const point = "touches" in event ? event.touches?.[0] : event;
    if (!point) return;

    const deltaX = point.clientX - resizeState.startX;
    const rawNext = resizeState.startWidth + deltaX;
    const bounded = Math.max(MIN_IMAGE_WIDTH, Math.min(resizeState.maxWidth, rawNext));

    updateAttributes({ width: Math.round(bounded) });
  };

  const startResize = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const point = "touches" in event ? event.touches?.[0] : event;
    if (!point) return;

    const pos = getPos?.();
    if (typeof pos === "number" && editor?.view?.dispatch) {
      const tr = editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, pos));
      editor.view.dispatch(tr);
    }

    const wrapper = wrapperRef.current;
    const editorArea = wrapper?.closest?.(".lh6-editorArea");
    const editorWidth = (editorArea?.clientWidth || 760) - 6;
    const maxWidth = Math.max(
      MIN_IMAGE_WIDTH,
      layout === "center" ? editorWidth : Math.floor(editorWidth * 0.58)
    );
    const startWidth = width || wrapper?.querySelector("img")?.clientWidth || Math.min(520, maxWidth);

    resizeStateRef.current = {
      startX: point.clientX,
      startWidth,
      maxWidth,
    };

    document.addEventListener("mousemove", onResizeMove);
    document.addEventListener("mouseup", stopResize);
    document.addEventListener("touchmove", onResizeMove, { passive: false });
    document.addEventListener("touchend", stopResize);
  };

  return (
    <NodeViewWrapper
      as="figure"
      ref={wrapperRef}
      className={`lh6-imageNode is-${layout} ${selected ? "is-selected" : ""}`}
      contentEditable={false}
      onMouseDown={selectNode}
      data-lh-image-layout={layout}
    >
      <img
        src={node.attrs?.src || ""}
        alt={node.attrs?.alt || ""}
        title={node.attrs?.title || ""}
        draggable={false}
        style={{
          width: width ? `${width}px` : "auto",
          maxWidth: "100%",
          height: "auto",
        }}
      />
      {selected ? (
        <button
          type="button"
          className="lh6-imageResizeHandle"
          onMouseDown={startResize}
          onTouchStart={startResize}
          aria-label="Bildgröße ändern"
          title="Bildgröße ändern"
        />
      ) : null}
    </NodeViewWrapper>
  );
};

const ResizableImageExtension = Node.create({
  name: "resizableImage",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      src: {
        default: "",
      },
      alt: {
        default: "",
      },
      title: {
        default: "",
      },
      width: {
        default: DEFAULT_RESIZABLE_IMAGE_WIDTH,
        parseHTML: (element) => {
          const fromData = element.getAttribute("data-width");
          const fromWidthAttr = element.getAttribute("width");
          const fromStyle = element.style?.width;
          return (
            toPxNumber(fromData) ||
            toPxNumber(fromWidthAttr) ||
            toPxNumber(fromStyle) ||
            DEFAULT_RESIZABLE_IMAGE_WIDTH
          );
        },
      },
      layout: {
        default: "center",
        parseHTML: readLayoutFromElement,
      },
    };
  },

  parseHTML() {
    return [{ tag: "img[src]" }];
  },

  renderHTML({ HTMLAttributes }) {
    const nextAttributes = { ...HTMLAttributes };
    const width = toPxNumber(nextAttributes.width);
    const layout = normalizeLayout(nextAttributes.layout);
    delete nextAttributes.width;
    delete nextAttributes.layout;
    delete nextAttributes["data-width"];
    delete nextAttributes["data-lh6-image"];
    delete nextAttributes.style;

    return [
      "img",
      mergeAttributes(nextAttributes, {
        width: width || undefined,
        "data-lh-image-layout": layout,
        class: `lh6-image-align-${layout}`,
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageNodeView);
  },

  addCommands() {
    return {
      insertResizableImage:
        (attrs = {}) =>
        ({ commands }) => {
          const safeSrc = (attrs.src || "").trim();
          if (!safeSrc) return false;

          return commands.insertContent({
            type: this.name,
            attrs: {
              src: safeSrc,
              alt: attrs.alt || "",
              title: attrs.title || "",
              width: toPxNumber(attrs.width) || DEFAULT_RESIZABLE_IMAGE_WIDTH,
              layout: normalizeLayout(attrs.layout),
            },
          });
        },
    };
  },
});

export default ResizableImageExtension;
